import { useMemo } from 'react'

import { useQuery } from '@tanstack/react-query'
import { CellValueChangedEvent } from 'ag-grid-enterprise'

import { AgGridWidget } from '@fortem-monorepo/react-ag'
import { useApi, useConfirmModal } from '@fortem-monorepo/react-spa'
import { useUserContext } from '@fortem-monorepo/react-spa'
import { Trades } from '@fortem-monorepo/web-sdk'

import { createOldDBColDefs } from '@/pages/trading/booked-trades/grids/olddb/coldefs'
import { createOlddbContextMenu } from '@/pages/trading/booked-trades/grids/olddb/contextmenu'
import { useApproveElementMutation } from '@/react-query/mutations/audit'
import {
  useDeleteOldDBTradeMutation,
  useUpdateOlddbTradeMutation,
} from '@/react-query/mutations/trades'
import { useInternalUser } from '@/react-query/queries/account'

export interface OlddbTradesGridProps {
  params: Trades.GetTradesParams
}

export const OlddbTradesGrid: React.FC<OlddbTradesGridProps> = ({ params }) => {
  const { internalUser } = useInternalUser()
  const userContext = useUserContext()
  const olddbColDefs = useMemo(() => createOldDBColDefs(userContext), [userContext.user])
  const updateOlddbTradeMutation = useUpdateOlddbTradeMutation()
  const deleteOldDBTradeMutation = useDeleteOldDBTradeMutation()
  const approveElementMutation = useApproveElementMutation()

  const api = useApi()

  const query = useQuery({
    queryKey: ['trades', 'olddb', params],
    queryFn: () => api.trades.getOlddbTrades(params),
    enabled: !!params?.st_dt,
  })

  const { Dialog: DeleteOldDBTradeConfirmation, withConfirmation } = useConfirmModal({
    title: 'Really delete this trade?',
    description: 'Are you sure you want to delete this trade?',
  })

  return (
    <>
      <DeleteOldDBTradeConfirmation />
      <AgGridWidget
        widget={{ exposed: true, className: 'h-full' }}
        widgetHeader={{ title: 'Olddb Trades' }}
        query={query}
        grid={{
          defaultCsvExportParams: {
            allColumns: true,
          },
          domLayout: 'normal',
          rowData: query.data,
          columnDefs: olddbColDefs,
          autoSizeStrategy: { type: 'fitCellContents' },
          getContextMenuItems: createOlddbContextMenu({
            withConfirmation,
            internalUser,
            approveMutation: approveElementMutation,
            deleteMutation: deleteOldDBTradeMutation,
          }),
          onCellValueChanged: (params: CellValueChangedEvent<Trades.OlddbTrade>) => {
            return updateOlddbTradeMutation.mutateAsync({
              rowData: params.data,
              updatedField: params.colDef.field,
              oldValue: params.oldValue,
              newValue: params.newValue,
            })
          },
        }}
      />
    </>
  )
}
